import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { ShellContext } from '../shell/shellContext';
import { useTab } from '../shell/TabContext';
import { authApi } from '../services/authApi';
import type { GenericDetail } from '../types';
import { formatApprovalDate } from '../utils/approvalDate';
import { WHO_INN_DISCLAIMER } from '../constants/whoInnDisclaimer';
import { regionDisplayLabel, isUsFdaVisible } from '../constants/filterConstants';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { resolveDraftTarget } from '../utils/resolveDraftTarget';

const draftHref = (drugId: number, versionId: number) => {
  const { path, query } = resolveDraftTarget(
    { drug_id: drugId, label_version_id: versionId },
    { kind: 'content' },
  );
  return `${path}?${new URLSearchParams(
    Object.entries(query).map(([k, v]) => [k, String(v)] as [string, string]),
  ).toString()}`;
};

const Badge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-xs font-medium">{children}</span>
);

const GenericPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const usFdaVisible = isUsFdaVisible(useIsAdmin());
  const genericId = id && /^\d+$/.test(id) ? Number(id) : null;

  const [data, setData] = useState<GenericDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(genericId !== null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggleRegion = (region: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(region)) next.delete(region);
      else next.add(region);
      return next;
    });

  useEffect(() => {
    if (genericId === null) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    authApi
      .getGeneric(genericId, controller.signal)
      .then((d: GenericDetail) => setData(d))
      .catch((err: { response?: { status?: number } }) => {
        if (controller.signal.aborted) return;
        setError(err?.response?.status === 404 ? 'Generic not found.' : 'Failed to load generic.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [genericId]);

  // Name this tab after the generic (see DrugDetailPage); `id` guards a stale `data`.
  const { tabId } = useTab();
  const shell = useContext(ShellContext);
  const tabTitle = shell?.tabs.find((t) => t.id === tabId)?.title;
  const tabName = data && data.id === genericId ? data.display_name : null;
  useEffect(() => {
    if (shell && tabName && tabTitle !== undefined && tabTitle !== tabName) {
      shell.setTabTitle(tabId, tabName);
    }
  }, [shell, tabId, tabTitle, tabName]);

  if (genericId === null) return <div className="p-6 text-slate-600">Generic not found.</div>;
  if (loading) return <div className="p-6 text-slate-500">Loading…</div>;
  if (error || !data) return <div className="p-6 text-slate-600">{error ?? 'Failed to load generic.'}</div>;

  const total = data.regions.reduce((n, r) => n + r.drugs.length, 0);

  return (
    <div className="min-h-full bg-slate-50">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <h1 className="text-lg font-bold text-slate-900">{data.display_name}</h1>
          <p className="text-sm text-slate-500">{total} {total === 1 ? 'product' : 'products'}</p>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {data.regions.map((group) => {
          const isOpen = !collapsed.has(group.region);
          return (
            <section key={group.region} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <h2 className={`text-sm font-semibold text-slate-700 bg-slate-50 ${isOpen ? 'border-b border-slate-200' : ''}`}>
                <button
                  type="button"
                  onClick={() => toggleRegion(group.region)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center gap-1.5 px-4 py-2 text-left hover:bg-slate-100"
                >
                  {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  {regionDisplayLabel(group.region, usFdaVisible)}{' '}
                  <span className="font-normal text-slate-500">({group.drugs.length})</span>
                </button>
              </h2>
              {isOpen && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500">
                      <th className="px-4 py-2 font-medium">Brand</th>
                      <th className="px-4 py-2 font-medium">Manufacturer</th>
                      <th className="px-4 py-2 font-medium">Approval date</th>
                      <th className="px-4 py-2 font-medium">Ingredients</th>
                    </tr>
                  </thead>
                  {group.drugs.map((d) => (
                    <tbody key={d.id} className="border-t border-slate-100">
                      <tr>
                        <td className="px-4 py-2">
                          <Link to={`/drug/${d.id}`} className="text-blue-600 hover:underline">
                            {d.brand_name ?? d.generic_name ?? 'Untitled'}
                          </Link>
                          {d.is_combination && <Badge>Combination</Badge>}
                          {d.partially_resolved && <Badge>Partially resolved</Badge>}
                        </td>
                        <td className="px-4 py-2 text-slate-700">{d.manufacturer ?? '—'}</td>
                        <td className="px-4 py-2 text-slate-700">
                          {formatApprovalDate(d.approval_date, d.approval_date_precision) ?? '—'}
                        </td>
                        <td className="px-4 py-2 text-slate-700">{d.source_texts.join(', ')}</td>
                      </tr>
                      {d.drafts.length > 0 && (
                        <tr>
                          <td colSpan={4} className="px-4 pb-2 text-xs text-slate-500">
                            <span>Label negotiation ({d.drafts.length})</span>
                            {d.drafts.map((dr) => (
                              <Link
                                key={dr.id}
                                to={draftHref(d.id, dr.id)}
                                className="ml-2 text-blue-600 hover:underline"
                              >
                                v{dr.version_number}
                              </Link>
                            ))}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  ))}
                </table>
              )}
            </section>
          );
        })}
        <p className="text-xs text-slate-500">{WHO_INN_DISCLAIMER}</p>
      </div>
    </div>
  );
};

export default GenericPage;
